import { RetryButton } from '@/shared/components/RetryButton';
import { fileReadMessage } from '../fileReadMessage';
import { useFileObjectUrl } from '../hooks/useFileObjectUrl';

/**
 * An uploaded image, shown as an image.
 *
 * The bytes come through `useFileObjectUrl` rather than `<img src={contentUrl}>`.
 * That direct form is what this component used to do, and it stopped working the
 * day access tokens became mandatory on the API: an `<img>` cannot send an
 * `Authorization` header, so the request 401'd and the picture became a broken
 * icon. Nothing reported it, because a failed image is silent — which is why it
 * went unnoticed for three weeks.
 *
 * The name and the download that used to sit in a strip under the picture are in
 * the preview header now, where every file kind has them.
 */
export function ImagePreview({
  workspaceId,
  path,
  name,
}: {
  workspaceId: string;
  path: string;
  name: string;
}) {
  const { url, failed, failureReason, retry } = useFileObjectUrl(
    workspaceId,
    path,
    true,
  );

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
      {failed ? (
        /*
          Said out loud, unlike a broken image icon — and said accurately: the
          reason is read, not discarded, so a deleted picture and a refused one
          no longer share a sentence (F17/FR-067). Shared wording with the HTML
          and PDF panes. The download in the preview header still works, so this
          names the one thing that did not.
        */
        <div className="flex flex-col items-center gap-3">
          <p className="text-muted-foreground text-sm">
            {fileReadMessage(failureReason, 'image')}
          </p>
          {/*
            Same rule as the PDF and HTML panes: only `failed` — a read that
            dropped in transit — is worth a second identical request. A deleted
            picture, a refused one and an expired session are answers.
          */}
          {failureReason === 'failed' ? <RetryButton onRetry={retry} /> : null}
        </div>
      ) : url === undefined ? (
        <div
          aria-hidden="true"
          className="bg-muted h-48 w-64 animate-pulse rounded-lg"
        />
      ) : (
        /* Contained rather than stretched: an image is evidence, and scaling
           it past its own resolution makes it look like something it is not. */
        <img
          src={url}
          alt={name}
          className="max-h-full max-w-full object-contain"
        />
      )}
    </div>
  );
}
